import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import AuthImg from "../components/AuthImg";
import { toast } from "sonner";
import { formatLocalDateTime } from "../lib/constants";
import {
  Wrench,
  ChevronLeft,
  PackageCheck,
  Undo2,
  Edit3,
  Trash2,
  AlertTriangle,
  Search,
  Camera,
  Upload,
  X,
  History as HistoryIcon,
  CalendarClock,
  QrCode,
  Printer,
} from "lucide-react";

const STATUS_PILL = {
  Available: "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Checked Out": "bg-amber-100 text-amber-800 border-amber-300",
  "In Calibration": "bg-blue-100 text-blue-800 border-blue-300",
  "Under Repair": "bg-slate-200 text-slate-800 border-slate-400",
  Retired: "bg-rose-100 text-rose-800 border-rose-300",
  // Legacy
  "In Yard": "bg-emerald-100 text-emerald-800 border-emerald-300",
  "In Use": "bg-blue-100 text-blue-800 border-blue-300",
  Maintenance: "bg-slate-200 text-slate-800 border-slate-400",
  Damaged: "bg-rose-100 text-rose-800 border-rose-300",
};

export default function EquipmentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { isRunnerLevel, isPrivileged } = useAuth();
  const [tool, setTool] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, h] = await Promise.all([
        api.get(`/equipment/${id}`),
        api.get(`/equipment/${id}/history`),
      ]);
      setTool(t.data);
      setHistory(h.data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Could not load tool");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onDelete = async () => {
    if (!confirm(`Delete tool ${tool.Tool_ID}? This cannot be undone.`)) return;
    try {
      await api.delete(`/equipment/${id}`);
      toast.success("Tool deleted");
      nav("/equipment");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  if (loading) return <div className="p-6 text-slate-500">Loading…</div>;
  if (error)
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Link to="/equipment" className="text-sm text-slate-600">
          ← Back
        </Link>
        <div className="rg-card p-6 mt-3 border-2 border-rose-300 text-rose-800">{error}</div>
      </div>
    );
  if (!tool) return null;

  const isCheckedOut = ["Checked Out", "In Use"].includes(tool.Current_Status);
  const canCheckOut = ["Available", "In Yard"].includes(tool.Current_Status);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-10">
      <Link
        to="/equipment"
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-2"
        data-testid="equipment-back"
      >
        <ChevronLeft className="h-4 w-4" /> Back to Equipment
      </Link>

      {/* Header */}
      <div className="rg-card p-4 sm:p-5 mb-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="h-12 w-12 grid place-items-center bg-slate-900 text-amber-400 rounded-sm flex-shrink-0">
              <Wrench strokeWidth={2.5} className="h-6 w-6" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-mono text-xs text-slate-600">{tool.Tool_ID}</div>
              <h1 className="font-display text-2xl sm:text-3xl uppercase text-slate-900 tracking-tight">
                {tool.Tool_Name}
              </h1>
              <div className="flex items-center gap-2 flex-wrap mt-1">
                <span
                  className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 border rounded-sm ${
                    STATUS_PILL[tool.Current_Status] || STATUS_PILL["In Yard"]
                  }`}
                  data-testid="tool-status-pill"
                >
                  {tool.Current_Status}
                </span>
                {tool.Category && (
                  <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-700 border border-slate-300 rounded-sm">
                    {tool.Category}
                  </span>
                )}
                {tool.calibration_state === "overdue" && (
                  <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-2 py-0.5 bg-rose-100 text-rose-800 border border-rose-400 rounded-sm">
                    <AlertTriangle className="h-3 w-3" />
                    Cal Overdue
                  </span>
                )}
                {tool.calibration_state === "due_soon" && (
                  <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 rounded-sm">
                    <CalendarClock className="h-3 w-3" />
                    Cal Due Soon
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={() => setQrOpen(true)}
              data-testid="tool-qr-btn"
              className="rg-tap px-3 border-2 border-slate-300 hover:border-slate-900 rounded-sm bg-white flex items-center gap-2 font-bold uppercase tracking-wide text-sm text-slate-700"
              title="Print QR sticker"
            >
              <QrCode className="h-4 w-4" />
              <span className="hidden sm:inline">QR</span>
            </button>
            {isRunnerLevel && (
              <Link
                to={`/equipment/${id}/edit`}
                data-testid="tool-edit-btn"
                className="rg-tap px-3 border-2 border-slate-300 hover:border-slate-900 rounded-sm bg-white flex items-center gap-2 font-bold uppercase tracking-wide text-sm text-slate-700"
              >
                <Edit3 className="h-4 w-4" /> Edit
              </Link>
            )}
            {isPrivileged && (
              <button
                onClick={onDelete}
                data-testid="tool-delete-btn"
                className="rg-tap px-3 border-2 border-rose-400 hover:border-rose-700 hover:bg-rose-50 rounded-sm bg-white flex items-center gap-2 font-bold uppercase tracking-wide text-sm text-rose-700"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <dl className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          <Info label="Serial #" value={tool.Serial_Number} />
          <Info label="Condition" value={tool.Condition} />
          <Info label="Location" value={tool.Location} />
          <Info label="Calibration due" value={tool.Calibration_Due} />
          <Info label="Last checked out" value={fmtTs(tool.Last_Checked_Out)} />
          <Info label="Expected return" value={tool.Expected_Return_Date} />
          <Info label="Last returned" value={fmtTs(tool.Last_Returned)} />
          {isCheckedOut && (
            <Info
              label="Currently with"
              value={tool.Assigned_To}
              valueClass="text-amber-700 font-bold"
            />
          )}
        </dl>

        {tool.Notes && (
          <div className="mt-3 text-sm bg-slate-50 border-l-4 border-slate-300 px-3 py-2 rounded-sm">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1">
              Notes
            </div>
            {tool.Notes}
          </div>
        )}
      </div>

      {/* Checkout / Return action buttons */}
      {isRunnerLevel && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
          {!isCheckedOut && canCheckOut && (
            <button
              onClick={() => setCheckoutOpen(true)}
              data-testid="tool-checkout-btn"
              className="rg-tap-xl w-full bg-amber-600 hover:bg-amber-700 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 min-h-[60px] text-base border-2 border-amber-800"
            >
              <PackageCheck strokeWidth={3} className="h-5 w-5" />
              Check Out
            </button>
          )}
          {isCheckedOut && (
            <button
              onClick={() => setReturnOpen(true)}
              data-testid="tool-return-btn"
              className="rg-tap-xl w-full bg-emerald-700 hover:bg-emerald-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 min-h-[60px] text-base border-2 border-emerald-900 sm:col-span-2"
            >
              <Undo2 strokeWidth={3} className="h-5 w-5" />
              Return Tool
            </button>
          )}
        </div>
      )}

      {/* Photos */}
      {tool.Images?.length > 0 && (
        <div className="mb-4">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-700 mb-2">
            Photos ({tool.Images.length})
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {tool.Images.map((p) => (
              <div key={p} className="aspect-square">
                <AuthImg
                  path={p}
                  className="w-full h-full object-cover border-2 border-slate-300 rounded-sm"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History */}
      <div className="rg-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <HistoryIcon className="h-4 w-4 text-slate-700" />
          <div className="font-display uppercase tracking-wide text-sm text-slate-800">
            History
          </div>
        </div>
        {history.length === 0 ? (
          <div className="text-sm text-slate-500">No events yet.</div>
        ) : (
          <ul className="space-y-2 text-sm" data-testid="tool-history">
            {history.map((h) => (
              <li key={h.id} className="border-l-4 border-slate-300 pl-3">
                <div className="font-bold text-slate-900">
                  {h.action.replace(/_/g, " ")}
                </div>
                <div className="text-xs text-slate-600">
                  {formatLocalDateTime(h.timestamp)} · {h.user_name}
                </div>
                {h.details && Object.keys(h.details).length > 0 && (
                  <pre className="text-xs text-slate-700 bg-slate-50 mt-1 px-2 py-1 rounded-sm whitespace-pre-wrap">
                    {JSON.stringify(h.details, null, 0)}
                  </pre>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Checkout dialog */}
      {checkoutOpen && (
        <CheckoutDialog
          tool={tool}
          onClose={() => setCheckoutOpen(false)}
          onDone={() => {
            setCheckoutOpen(false);
            load();
          }}
        />
      )}
      {returnOpen && (
        <ReturnDialog
          tool={tool}
          onClose={() => setReturnOpen(false)}
          onDone={() => {
            setReturnOpen(false);
            load();
          }}
        />
      )}
      {qrOpen && <QrStickerDialog tool={tool} onClose={() => setQrOpen(false)} />}
    </div>
  );
}

function Info({ label, value, valueClass = "" }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
        {label}
      </div>
      <div className={`text-sm text-slate-900 ${valueClass}`}>{value || "—"}</div>
    </div>
  );
}

function fmtTs(iso) {
  if (!iso) return "";
  try {
    return formatLocalDateTime(iso);
  } catch {
    return iso;
  }
}

// ------------------ Checkout Dialog ------------------
function CheckoutDialog({ tool, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null); // assignee object
  const [notes, setNotes] = useState("");
  const [expectedReturn, setExpectedReturn] = useState("");
  const [busy, setBusy] = useState(false);
  const [linkedItem, setLinkedItem] = useState(null);
  const debounceRef = useRef(null);

  // If tool is linked to an inventory item, pull a tiny preview for the deduction notice.
  useEffect(() => {
    if (!tool?.Inventory_Item_Id) {
      setLinkedItem(null);
      return;
    }
    let cancelled = false;
    api
      .get("/inventory", { params: { limit: 2000 } })
      .then(({ data }) => {
        if (cancelled) return;
        const it = (data || []).find((x) => x.id === tool.Inventory_Item_Id);
        setLinkedItem(it || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [tool?.Inventory_Item_Id]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/equipment/assignees", { params: { q } });
        setResults(data);
      } catch {
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const submit = async () => {
    if (busy) return;
    if (!picked && !q.trim()) {
      toast.error("Pick a user, crew leader, or type a name.");
      return;
    }
    setBusy(true);
    try {
      const body = picked
        ? {
            assigned_to: picked.name,
            assigned_to_email: picked.email,
            assigned_to_type: picked.type,
            expected_return_date: expectedReturn,
            notes,
          }
        : { assigned_to: q.trim(), expected_return_date: expectedReturn, notes };
      await api.post(`/equipment/${tool.id}/checkout`, body);
      toast.success(`${tool.Tool_Name} checked out`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-end sm:place-items-center bg-slate-900/60 backdrop-blur-sm p-0 sm:p-4"
      data-testid="checkout-modal"
      onClick={(e) => e.target === e.currentTarget && !busy && onClose()}
    >
      <div className="bg-white w-full sm:max-w-lg rounded-t-lg sm:rounded-md border-t-4 sm:border-2 border-slate-900 shadow-2xl flex flex-col max-h-[92vh]">
        <div className="px-5 py-4 border-b-2 border-slate-200">
          <h2 className="font-display text-lg uppercase text-slate-900">Check Out Tool</h2>
          <p className="text-xs text-slate-600 mt-0.5">
            {tool.Tool_ID} · {tool.Tool_Name}
          </p>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <label className="block">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-700 mb-1">
              Assign to (search users + crew leaders)
            </div>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                value={picked ? picked.label : q}
                onChange={(e) => {
                  setPicked(null);
                  setQ(e.target.value);
                }}
                data-testid="checkout-assignee-search"
                placeholder="Search by name or email…"
                className="rg-tap w-full pl-9 pr-3 border-2 border-slate-300 rounded-sm bg-white focus:border-slate-900 outline-none"
                autoFocus
              />
            </div>
          </label>
          {!picked && q.trim() && (
            <div
              className="border-2 border-slate-200 rounded-sm max-h-48 overflow-y-auto"
              data-testid="checkout-assignee-results"
            >
              {results.length === 0 ? (
                <div className="px-3 py-2 text-sm text-slate-500">
                  No matches. The text above will be used as a free-text assignee.
                </div>
              ) : (
                results.map((r) => (
                  <button
                    key={`${r.type}-${r.id}`}
                    type="button"
                    onClick={() => {
                      setPicked(r);
                      setQ("");
                    }}
                    data-testid={`checkout-pick-${r.type}-${r.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-amber-50 border-b last:border-b-0 border-slate-100"
                  >
                    <div className="font-bold text-slate-900">{r.name}</div>
                    <div className="text-xs text-slate-600">
                      {r.email && <span>{r.email} · </span>}
                      <span className="uppercase tracking-wider text-amber-700">
                        {r.type === "crew_leader" ? "Crew Leader" : r.role || "User"}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
          {picked && (
            <div className="border-2 border-emerald-300 bg-emerald-50 px-3 py-2 rounded-sm text-sm flex items-center gap-2">
              <PackageCheck className="h-4 w-4 text-emerald-700" />
              <span>
                Checking out to <strong>{picked.name}</strong>{" "}
                <span className="text-slate-600">({picked.type === "crew_leader" ? "Crew Leader" : picked.role})</span>
              </span>
            </div>
          )}
          {linkedItem && (
            <div
              className="border-2 border-amber-300 bg-amber-50 px-3 py-2 rounded-sm text-sm flex items-start gap-2"
              data-testid="checkout-linked-inventory-notice"
            >
              <PackageCheck className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <strong className="block">Auto-stock deduction</strong>
                <span className="text-slate-700">
                  This tool is linked to inventory item{" "}
                  <strong>{linkedItem.Item_Name}</strong> (current stock:{" "}
                  <strong>{linkedItem.Current_Stock}</strong>). Stock will drop to{" "}
                  <strong>{Math.max(0, (linkedItem.Current_Stock || 0) - 1)}</strong> on checkout
                  and restore on return.
                </span>
              </div>
            </div>
          )}
          <label className="block">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-700 mb-1">
              Expected return date (optional)
            </div>
            <input
              type="date"
              value={expectedReturn}
              onChange={(e) => setExpectedReturn(e.target.value)}
              data-testid="checkout-expected-return"
              className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 bg-white focus:border-slate-900 outline-none"
            />
          </label>
          <label className="block">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-700 mb-1">
              Notes (optional)
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="checkout-notes"
              rows={2}
              className="w-full border-2 border-slate-300 rounded-sm px-3 py-2 text-base bg-white focus:border-slate-900 outline-none"
              placeholder="e.g. for TSS 12 install"
            />
          </label>
        </div>
        <div className="p-3 border-t-2 border-slate-200 grid grid-cols-2 gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            data-testid="checkout-cancel-btn"
            className="rg-tap-xl w-full bg-white text-slate-800 font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 min-h-[56px] border-2 border-slate-300"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="checkout-confirm-btn"
            className="rg-tap-xl w-full bg-amber-600 hover:bg-amber-700 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 min-h-[56px] border-2 border-amber-800 disabled:opacity-60"
          >
            <PackageCheck strokeWidth={3} className="h-5 w-5" />
            {busy ? "Checking out…" : "Confirm Checkout"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------ Return Dialog ------------------
function ReturnDialog({ tool, onClose, onDone }) {
  const [condition, setCondition] = useState("Good");
  const [setStatus, setSetStatus] = useState("In Yard");
  const [notes, setNotes] = useState("");
  const [images, setImages] = useState([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setImageUploading(true);
    try {
      const newPaths = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newPaths.push(data.path);
      }
      setImages((cur) => [...cur, ...newPaths]);
      toast.success(`Uploaded ${files.length} photo${files.length > 1 ? "s" : ""}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setImageUploading(false);
      e.target.value = "";
    }
  };

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/equipment/${tool.id}/return`, {
        condition,
        notes,
        images,
        set_status: setStatus,
      });
      toast.success(`${tool.Tool_Name} returned`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Return failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-end sm:place-items-center bg-slate-900/60 backdrop-blur-sm p-0 sm:p-4"
      data-testid="return-modal"
      onClick={(e) => e.target === e.currentTarget && !busy && onClose()}
    >
      <div className="bg-white w-full sm:max-w-lg rounded-t-lg sm:rounded-md border-t-4 sm:border-2 border-slate-900 shadow-2xl flex flex-col max-h-[92vh]">
        <div className="px-5 py-4 border-b-2 border-slate-200">
          <h2 className="font-display text-lg uppercase text-slate-900">Return Tool</h2>
          <p className="text-xs text-slate-600 mt-0.5">
            {tool.Tool_ID} · {tool.Tool_Name} · was with{" "}
            <strong>{tool.Assigned_To || "—"}</strong>
          </p>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-700 mb-1">
                Condition on return
              </div>
              <select
                value={condition}
                onChange={(e) => setCondition(e.target.value)}
                data-testid="return-condition"
                className="rg-tap w-full border-2 border-slate-300 rounded-sm px-2 bg-white focus:border-slate-900 outline-none"
              >
                <option>Good</option>
                <option>Fair</option>
                <option>Needs Service</option>
                <option>Damaged</option>
              </select>
            </label>
            <label className="block">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-700 mb-1">
                Set status to
              </div>
              <select
                value={setStatus}
                onChange={(e) => setSetStatus(e.target.value)}
                data-testid="return-set-status"
                className="rg-tap w-full border-2 border-slate-300 rounded-sm px-2 bg-white focus:border-slate-900 outline-none"
              >
                <option>Available</option>
                <option>In Calibration</option>
                <option>Under Repair</option>
                <option>Retired</option>
              </select>
            </label>
          </div>
          <label className="block">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-700 mb-1">
              Photo of returned tool (recommended)
            </div>
            <label
              htmlFor="return-photo-input"
              data-testid="return-photo-label"
              className="rg-tap w-full bg-white border-2 border-dashed border-slate-400 hover:border-slate-900 rounded-sm flex items-center justify-center gap-2 font-bold uppercase tracking-wide text-slate-700 cursor-pointer min-h-[60px]"
            >
              <Camera strokeWidth={2.5} className="h-5 w-5" />
              {imageUploading ? "Uploading…" : "Take return photo"}
            </label>
            <input
              id="return-photo-input"
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              onChange={onUpload}
              className="hidden"
              data-testid="return-photo-input"
            />
            {images.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-2">
                {images.map((p) => (
                  <div key={p} className="relative aspect-square">
                    <AuthImg
                      path={p}
                      className="w-full h-full object-cover border-2 border-slate-300 rounded-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setImages((cur) => cur.filter((x) => x !== p))}
                      className="absolute -top-2 -right-2 h-6 w-6 bg-rose-700 text-white grid place-items-center rounded-sm border-2 border-white"
                    >
                      <X strokeWidth={3} className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </label>
          <label className="block">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-700 mb-1">
              Notes (optional)
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="return-notes"
              rows={2}
              className="w-full border-2 border-slate-300 rounded-sm px-3 py-2 text-base bg-white focus:border-slate-900 outline-none"
              placeholder="e.g. battery dead, calibration looks off"
            />
          </label>
        </div>
        <div className="p-3 border-t-2 border-slate-200 grid grid-cols-2 gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            data-testid="return-cancel-btn"
            className="rg-tap-xl w-full bg-white text-slate-800 font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 min-h-[56px] border-2 border-slate-300"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || imageUploading}
            data-testid="return-confirm-btn"
            className="rg-tap-xl w-full bg-emerald-700 hover:bg-emerald-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 min-h-[56px] border-2 border-emerald-900 disabled:opacity-60"
          >
            <Undo2 strokeWidth={3} className="h-5 w-5" />
            {busy ? "Returning…" : "Confirm Return"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------ QR Sticker Print ------------------
function QrStickerDialog({ tool, onClose }) {
  const [qrSrc, setQrSrc] = useState("");

  // Authenticated fetch → blob URL so the printable image works without sending the bearer token
  useEffect(() => {
    let revoke;
    (async () => {
      try {
        const { data } = await api.get(`/equipment/${tool.id}/qr.png`, {
          responseType: "blob",
        });
        const url = URL.createObjectURL(data);
        revoke = url;
        setQrSrc(url);
      } catch {
        toast.error("Could not load QR");
      }
    })();
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [tool.id]);

  const onPrint = () => {
    const w = window.open("", "_blank", "width=480,height=600");
    if (!w) {
      toast.error("Pop-up blocked — allow pop-ups to print");
      return;
    }
    const calLabel = {
      overdue: "CAL OVERDUE",
      due_soon: "CAL DUE SOON",
      ok: "CAL OK",
      none: "",
    }[tool.calibration_state] || "";
    const calClass = tool.calibration_state || "";

    // Build the document via DOM APIs (no document.write, no string-interpolated user data) — XSS safe.
    const d = w.document;
    d.open();
    d.close();
    d.title = `${tool.Tool_ID || "tool"} sticker`;

    const style = d.createElement("style");
    style.textContent = `
      @page { size: 70mm 38mm; margin: 0; }
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
      .label { width: 70mm; height: 38mm; padding: 3mm; display: flex; gap: 3mm; align-items: center; border: 1pt solid #000; }
      .qr { width: 28mm; height: 28mm; flex: 0 0 28mm; }
      .qr img { width: 100%; height: 100%; }
      .meta { flex: 1; min-width: 0; }
      .id { font-family: ui-monospace, monospace; font-size: 9pt; font-weight: 700; }
      .nm { font-weight: 800; font-size: 11pt; line-height: 1.15; margin-top: 1mm; word-break: break-word; }
      .due { font-size: 8pt; margin-top: 1.5mm; }
      .pill { display: inline-block; padding: 0.4mm 1.2mm; font-size: 7pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; border: 0.5pt solid; border-radius: 0.5mm; margin-top: 1mm; }
      .pill.overdue { background:#fee2e2; color:#9f1239; border-color:#fca5a5; }
      .pill.due_soon { background:#fef3c7; color:#92400e; border-color:#fcd34d; }
      .pill.ok { background:#d1fae5; color:#047857; border-color:#86efac; }
    `;
    d.head.appendChild(style);

    const label = d.createElement("div");
    label.className = "label";

    const qrWrap = d.createElement("div");
    qrWrap.className = "qr";
    const qrImg = d.createElement("img");
    qrImg.alt = "QR";
    qrImg.src = qrSrc; // blob URL produced by our own code
    qrWrap.appendChild(qrImg);

    const meta = d.createElement("div");
    meta.className = "meta";

    const idEl = d.createElement("div");
    idEl.className = "id";
    idEl.textContent = tool.Tool_ID || "";

    const nameEl = d.createElement("div");
    nameEl.className = "nm";
    nameEl.textContent = tool.Tool_Name || "";

    meta.append(idEl, nameEl);

    if (tool.Calibration_Due) {
      const dueEl = d.createElement("div");
      dueEl.className = "due";
      dueEl.textContent = `Cal due ${tool.Calibration_Due}`;
      meta.appendChild(dueEl);
    }
    if (calLabel) {
      const pill = d.createElement("span");
      pill.className = `pill ${calClass}`;
      pill.textContent = calLabel;
      meta.appendChild(pill);
    }

    label.append(qrWrap, meta);
    d.body.appendChild(label);

    // Trigger print after the QR image loads (or after a short fallback delay).
    const triggerPrint = () => {
      try {
        w.focus();
        w.print();
      } catch (e) {
        // User closed the pop-up before print fired — nothing to do; log it.
        console.error("Sticker print failed (pop-up likely closed)", e);
      }
    };
    if (qrImg.complete) {
      setTimeout(triggerPrint, 200);
    } else {
      qrImg.addEventListener("load", () => setTimeout(triggerPrint, 100));
      qrImg.addEventListener("error", triggerPrint);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4"
      data-testid="qr-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white w-full sm:max-w-md rounded-md border-2 border-slate-900 shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b-2 border-slate-200 flex items-center gap-3">
          <QrCode className="h-5 w-5 text-slate-700" />
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-lg uppercase text-slate-900">QR Sticker</h2>
            <p className="text-xs text-slate-600 truncate">
              {tool.Tool_ID} · {tool.Tool_Name}
            </p>
          </div>
        </div>
        <div className="p-5 grid place-items-center">
          {qrSrc ? (
            <img
              src={qrSrc}
              alt={`QR for ${tool.Tool_ID}`}
              data-testid="qr-image"
              className="w-56 h-56 border-2 border-slate-200"
            />
          ) : (
            <div className="w-56 h-56 grid place-items-center bg-slate-100 text-slate-500 text-sm">
              Generating…
            </div>
          )}
          <div className="mt-3 text-center">
            <div className="font-mono text-sm text-slate-600">{tool.Tool_ID}</div>
            <div className="font-bold text-slate-900">{tool.Tool_Name}</div>
            {tool.Calibration_Due && (
              <div className="text-xs text-slate-600 mt-0.5">
                Cal due <strong>{tool.Calibration_Due}</strong>
              </div>
            )}
          </div>
        </div>
        <div className="p-3 border-t-2 border-slate-200 grid grid-cols-2 gap-2">
          <button
            onClick={onClose}
            data-testid="qr-close-btn"
            className="rg-tap-xl w-full bg-white text-slate-800 font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 min-h-[56px] border-2 border-slate-300"
          >
            Close
          </button>
          <button
            onClick={onPrint}
            data-testid="qr-print-btn"
            disabled={!qrSrc}
            className="rg-tap-xl w-full bg-slate-900 hover:bg-slate-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 min-h-[56px] border-2 border-slate-900 disabled:opacity-60"
          >
            <Printer className="h-5 w-5" />
            Print Sticker
          </button>
        </div>
      </div>
    </div>
  );
}

